<template>
  <v-card class="pa-4 elevation-1">
    <div class="d-flex align-center justify-space-between flex-wrap mb-2">
      <v-card-title class="text-subtitle-1 font-weight-bold pa-0">
        {{ $t('surveys.campaigns.title') }}
      </v-card-title>
      <div class="d-flex align-center">
        <vue-json-to-csv
          :json-data="exportRows"
          :labels="exportLabels"
          :csv-title="exportFilename"
          separator=";"
        >
          <v-btn
            text
            small
            color="deep-purple lighten-1"
            class="mr-2"
            :disabled="!campaigns.length"
          >
            <v-icon left small>mdi-download</v-icon>
            {{ $t('surveys.export.button') }}
          </v-btn>
        </vue-json-to-csv>
        <v-btn color="deep-purple" dark small @click="$emit('create')">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('surveys.campaigns.new') }}
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="campaigns"
      :loading="loading"
      :items-per-page="10"
      :no-data-text="$t('surveys.campaigns.empty')"
      item-key="id"
      class="elevation-0"
      @click:row="$emit('select', $event)"
    >
      <!--
        Verificado contra vuetify 2.4 (VDataTable.js:495): `click:row` emite
        (item, data), así que `$event` ES la fila, no el PointerEvent. No lo
        cambies "por las dudas".
      -->
      <template v-slot:item.name="{ item }">
        <span :class="{ 'font-weight-bold': item.id === selectedId }">{{ item.name }}</span>
      </template>

      <template v-slot:item.startsAt="{ item }">{{ item.startsAt || '—' }}</template>
      <template v-slot:item.endsAt="{ item }">{{ item.endsAt || '—' }}</template>

      <template v-slot:item.status="{ item }">
        <v-chip :color="statusColor(item.status)" small dark>
          {{ $t(`surveys.campaigns.status.${item.status}`) }}
        </v-chip>
      </template>

      <template v-slot:item.countries="{ item }">{{ countryLabel(item) }}</template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click.stop="$emit('edit', item)">mdi-pencil</v-icon>
        <v-icon
          small
          class="mr-2"
          @click.stop="$emit('toggle', item)"
        >
          {{ item.active ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}
        </v-icon>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-icon
                small
                :disabled="item.responseCount > 0"
                @click.stop="$emit('remove', item)"
              >
                mdi-delete
              </v-icon>
            </span>
          </template>
          <span>
            {{
              item.responseCount > 0
                ? $t('surveys.campaigns.delete.has_responses')
                : $t('surveys.campaigns.delete.title')
            }}
          </span>
        </v-tooltip>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv';

const STATUS_COLORS = {
  ACTIVE: 'green',
  SCHEDULED: 'blue-grey',
  FINISHED: 'grey',
  INACTIVE: 'grey darken-3',
};

export default {
  name: 'CampaignsTable',
  components: { VueJsonToCsv },
  props: {
    campaigns: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    selectedId: { type: Number, default: null },
    countries: { type: Array, default: () => [] },
  },
  computed: {
    headers() {
      return [
        { text: this.$t('surveys.campaigns.col_name'), value: 'name', align: 'start' },
        { text: this.$t('surveys.campaigns.col_starts_at'), value: 'startsAt' },
        { text: this.$t('surveys.campaigns.col_ends_at'), value: 'endsAt' },
        { text: this.$t('surveys.campaigns.col_status'), value: 'status' },
        {
          text: this.$t('surveys.campaigns.col_responses'),
          value: 'responseCount',
          align: 'center',
        },
        {
          text: this.$t('surveys.campaigns.col_countries'),
          value: 'countries',
          sortable: false,
        },
        {
          text: this.$t('surveys.campaigns.col_actions'),
          value: 'actions',
          sortable: false,
          align: 'end',
        },
      ];
    },
    exportRows() {
      return this.campaigns.map((campaign) => ({
        name: campaign.name,
        startsAt: campaign.startsAt || '—',
        endsAt: campaign.endsAt || '—',
        status: this.$t(`surveys.campaigns.status.${campaign.status}`),
        responseCount: campaign.responseCount,
        countries: this.countryLabel(campaign),
      }));
    },
    exportLabels() {
      return {
        name: { title: this.$t('surveys.campaigns.col_name') },
        startsAt: { title: this.$t('surveys.campaigns.col_starts_at') },
        endsAt: { title: this.$t('surveys.campaigns.col_ends_at') },
        status: { title: this.$t('surveys.campaigns.col_status') },
        responseCount: { title: this.$t('surveys.campaigns.col_responses') },
        countries: { title: this.$t('surveys.campaigns.col_countries') },
      };
    },
    exportFilename() {
      return `campanas_${new Date().toISOString().slice(0, 10)}`;
    },
  },
  methods: {
    // El borrado está deshabilitado si la campaña tiene respuestas: el backend
    // lo rechaza con 409 (la FK de satisfacción es ON DELETE CASCADE y se
    // llevaría las respuestas puestas).
    statusColor(status) {
      return STATUS_COLORS[status] || 'grey';
    },
    countryLabel(campaign) {
      if (!campaign.countryIds || !campaign.countryIds.length) {
        return this.$t('surveys.campaigns.all_countries');
      }
      const byId = new Map(this.countries.map((c) => [c.id, c.name]));
      return campaign.countryIds.map((id) => byId.get(id) || id).join(', ');
    },
  },
};
</script>
